import { Injectable } from '@angular/core';
import { Observable, map, tap } from 'rxjs';
import { LaunchdarklyService } from '../api/launchdarkly.service';

/**
 * Anything with a full Go CRUD surface, i.e. an entry in every one of the exhaustive
 * `Record<MigrationEntity, ...>` lookup tables in `DualApiService`. Adding a member here
 * makes the compiler demand all twelve entries, which is the point.
 *
 * All three coverage entities joined this union as their lambdas gained
 * getAll/getById/search/create/update/delete: `eeseCoverage`, then
 * `softwareVersionCoverage`, then `specificationCoverage`.
 */
export type MigrationEntity =
  | 'testCases' | 'testSuites' | 'testAnalyses' | 'testSpecifications'
  | 'eeseCoverage' | 'softwareVersionCoverage' | 'specificationCoverage';

/**
 * Coverage screens whose *evaluation* read is migrated but whose entity CRUD is not, so they
 * have no entry in the `DualApiService` tables. Empty now that all three coverage entities
 * have a full Go CRUD surface; kept as a named type so an evaluation-only area can be added
 * back without reshaping `MigrationArea`.
 */
export type MigrationCoverage = never;

/** Anything the migration flags and DiffLogger can be scoped to. */
export type MigrationArea = MigrationEntity | MigrationCoverage;
export type DisplaySource = 'old' | 'new';

/**
 * Which API(s) a call goes to.
 *  - `old`  — C# API only. The new API is never called.
 *  - `new`  — Go Lambda only, for every call that has a Go equivalent.
 *  - `both` — call both, compare via DiffLogger, render whichever `displaySource` selects.
 */
export type MigrationMode = 'old' | 'new' | 'both';

const MIGRATION_MODES: readonly MigrationMode[] = ['old', 'new', 'both'];

export interface EntityMigrationConfig {
  mode: MigrationMode;
  /** Only consulted when `mode === 'both'`. */
  displaySource: DisplaySource;
  /** @deprecated Derived from `mode`; prefer switching on `mode` directly. */
  dualReadEnabled: boolean;
  /** @deprecated Derived from `mode`; prefer switching on `mode` directly. */
  dualWriteEnabled: boolean;
}

/**
 * Reads the C# → Go Lambda migration flags from LaunchDarkly.
 * Flag names are shared with the Executor and booking clients so the rollout can be
 * coordinated.
 *  - `fenix-migration-mode`: 'old' | 'new' | 'both' — primary control (see MigrationMode).
 *  - `fenix-migration-display-source`: 'old' | 'new' — in `both` mode only, which of the
 *    two responses the UI sees. It has no effect in `old` or `new`, where the mode already
 *    names the single source.
 *
 * `fenix-migration-dual-api` is retired and no longer read. `fenix-migration-mode` must
 * exist in every LD environment before this ships — with no flag set the mode resolves to
 * `old` and the Go API is never called.
 */
@Injectable({
  providedIn: 'root'
})
export class MigrationConfigService {

  constructor(private launchdarklyService: LaunchdarklyService) {}

  getEntityConfig$(_entity: MigrationArea): Observable<EntityMigrationConfig> {
    return this.launchdarklyService.launchDarklyFlags$.pipe(
      map(flags => {
        const mode = this.resolveMode(flags);
        return {
          mode,
          displaySource: (flags['fenix-migration-display-source'] as DisplaySource) ?? 'old',
          dualReadEnabled: mode === 'both',
          dualWriteEnabled: mode === 'both',
        } as EntityMigrationConfig;
      }),
      tap(config => console.log(`[Migration] Config resolved:`, config))
    );
  }

  getMode$(entity: MigrationArea): Observable<MigrationMode> {
    return this.getEntityConfig$(entity).pipe(map(c => c.mode));
  }

  /**
   * True when the Go API is called at all — i.e. mode `new` or `both`. Use this to gate
   * Go-only side calls, which are valid in both of those modes but not in `old`.
   */
  isNewApiEnabled$(entity: MigrationArea): Observable<boolean> {
    return this.getEntityConfig$(entity).pipe(map(c => c.mode !== 'old'));
  }

  /** @deprecated True only in `both` mode. Prefer `getMode$` / `isNewApiEnabled$`. */
  isDualReadEnabled$(entity: MigrationArea): Observable<boolean> {
    return this.getEntityConfig$(entity).pipe(map(c => c.dualReadEnabled));
  }

  /** @deprecated True only in `both` mode. Prefer `getMode$` / `isNewApiEnabled$`. */
  isDualWriteEnabled$(entity: MigrationArea): Observable<boolean> {
    return this.getEntityConfig$(entity).pipe(map(c => c.dualWriteEnabled));
  }

  getDisplaySource$(entity: MigrationArea): Observable<DisplaySource> {
    return this.getEntityConfig$(entity).pipe(map(c => c.displaySource));
  }

  /**
   * `fenix-migration-mode` is the only flag consulted — `fenix-migration-dual-api` is
   * retired and no longer read.
   *
   * Anything other than a recognised mode resolves to `old`, so an environment where the
   * flag was never created, or where it holds a typo, keeps every call on the C# API rather
   * than guessing. Both cases warn, because a silent `old` looks identical to a deliberate
   * one and is otherwise invisible.
   */
  private resolveMode(flags: { [key: string]: any }): MigrationMode {
    const raw = flags['fenix-migration-mode'];

    if (MIGRATION_MODES.includes(raw as MigrationMode)) {
      return raw as MigrationMode;
    }

    console.warn(
      raw == null
        ? `[Migration] fenix-migration-mode is not set — defaulting to 'old' (C# API only)`
        : `[Migration] Unknown fenix-migration-mode value '${raw}' — defaulting to 'old' (C# API only)`
    );
    return 'old';
  }
}
